import { Outlet } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { EmployeesRootPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import { useQuery } from 'modules/core/hooks/useQuery'
import FiltersForm from 'modules/core/lib/forms/Filters'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { ContractsImporterProvider } from 'modules/employees/components/ContractImporter/contractsImporterContext'
import CollectionsBootstrap from 'modules/employees/lib/CollectionsBootstrap'
import { EmployeesImporterProvider } from 'modules/employees/lib/employeesImporterContext'
import jobLevelsCollection from 'modules/job_catalog/store/collections/levels'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import jobSalaryRangesCollection from 'modules/job_catalog/store/collections/salaryRanges'
import gendersCollection from 'modules/settings/store/collections/genders'
import legalGendersCollection from 'modules/settings/store/collections/legalGenders'

const Container = () => {
  const { company: currentCompany, employee } = useCurrentSession()

  const canSeeEmployees = employee ? employee.hasRegularAccess : true

  const [localFilterByValue] = useLocalStorage(
    LOCAL_STORAGE_KEYS.employeesFilter,
    'activity___active'
  )

  const { loading } = useAsyncData(
    () =>
      Promise.all([
        jobRolesCollection.fetch(),
        jobLevelsCollection.fetch(),
        jobSalaryRangesCollection.fetch(),
      ]),
    []
  )

  const { loading: loadingGenders } = useAsyncData(
    () => Promise.all([gendersCollection.fetch(), legalGendersCollection.fetch()]),
    []
  )

  const { loading: loadingModuleAvailabilities, moduleAvailabilities } = useModuleAvailabilities()
  const {
    loading: loadingGoalsV1FeatureAvailability,
    featureAvailability: goalsV1FeatureAvailability,
  } = useFeatureAvailability('goals_core', currentCompany.id)

  const { loading: loadingPage, data: pageData } = useQuery(EmployeesRootPageDocument)

  const hasPerformanceFullVisiblity = Boolean(
    pageData?.performance?._policies?.reviewProcessFullVisibilityContextless?.can
  )

  const filtersForm = useMemo(
    () =>
      new FiltersForm({
        sortAttribute: 'first_name',
        sortDirection: 'asc',
        filterBy: localFilterByValue || '',
      }),
    [localFilterByValue]
  )

  return (
    <AuthorizeUser authorizeFn={() => canSeeEmployees}>
      <Outlet
        context={{
          filtersForm,
          loading: loading || loadingPage,
          loadingGenders,
          loadingModuleAvailabilities,
          moduleAvailabilities,
          loadingGoalsV1FeatureAvailability,
          goalsV1FeatureAvailability,
          hasPerformanceFullVisiblity,
        }}
      />
    </AuthorizeUser>
  )
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <EmployeesImporterProvider>
        <ContractsImporterProvider>
          <Container />
        </ContractsImporterProvider>
      </EmployeesImporterProvider>
    </CollectionsBootstrap>
  )
}

export default RootPage
